/* eslint-disable @typescript-eslint/no-floating-promises */
import type { Dispatch, SetStateAction, ReactElement } from "react";
// eslint-disable-next-line no-duplicate-imports
import { useEffect, useState } from "react";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { Toast } from "@capacitor/toast";
import { NavigationMVP } from "./NavigationMVP";
import { Button } from "../ui/button";
import { useNavigate } from "@tanstack/react-router";
import { PushNotifications } from "@capacitor/push-notifications";
import { useQuery } from "@tanstack/react-query";
// import type { URLOpenListenerEvent } from "@capacitor/app";
// import { App } from "@capacitor/app";
import type { NotificationHistory } from "../../api/api";
// eslint-disable-next-line no-duplicate-imports
import { getNotificationsHistory } from "../../api/api";
import { useOs } from "@mantine/hooks";
import {
	getTopicSubscription,
	setTopicSubscription,
} from "@/utils/storage/topicStorage";
import { Capacitor } from "@capacitor/core";
import { BrokenNetworkConnectionIcon } from "@/components/icons/brokenNetworkConnection";
import { useOnlineStatusContext } from "@/context/OnlineStatusContext";
// import OneSignal from "onesignal-cordova-plugin";

const ensureAndroidChannel = async (): Promise<void> => {
	if (Capacitor.getPlatform() === "android") {
		await PushNotifications.createChannel({
			id: "articles",
			name: "Articles",
			description: "Article notifications",
			importance: 5,
			sound: "default",
			vibration: true,
			visibility: 1,
			lights: true,
		});
	}
};

const notificationPermission = (
	setNativePermission: Dispatch<
		SetStateAction<
			"denied" | "granted" | "prompt" | "prompt-with-rationale" | "unknown"
		>
	>,
	notificationRedirect: (route: string) => void,
	setNotificationCenterStatus: Dispatch<SetStateAction<"ready" | "notReady">>
): void => {
	FirebaseMessaging.checkPermissions()
		.then(async (result) => {
			console.log("NOTIFICATION STEP 1: PERMISSIONS CHECK");
			if (result.receive !== "granted") {
				return FirebaseMessaging.requestPermissions();
			}
			return result;
		})
		.then(async (result) => {
			if (!result || result.receive === "denied") {
				setNativePermission("denied");
				return;
			}

			// 1) Kanał ANDROID
			await ensureAndroidChannel();

			// 2) Wymuś token (auto-init OFF → bez tego bywa brak tokenu)
			const { token } = await FirebaseMessaging.getToken();
			console.log("FCM token:", token);

			setNativePermission(result.receive);

			// 3) Subskrypcja topic
			const topicSubscription = await getTopicSubscription();
			let showToast = false;
			
			if (topicSubscription === null || topicSubscription === true) {
				await FirebaseMessaging.subscribeToTopic({ topic: "all_users" });
				// Pokaż toast tylko jeśli to pierwsza konfiguracja (topicSubscription === null)
				if (topicSubscription === null) {
					showToast = true;
				}
				await setTopicSubscription(true);
			} else if (topicSubscription === false) {
				await FirebaseMessaging.unsubscribeFromTopic({ topic: "all_users" });
				await setTopicSubscription(false);
			}

			setNotificationCenterStatus("ready");
			
			// Pokaż toast tylko przy pierwszej konfiguracji
			if (showToast) {
				await Toast.show({ text: "Notyfikacje włączone" });
			}

			// opcjonalnie: zapisz token na backendzie (jeśli dodasz metodę)
			// await saveDeviceToken(token);
		})
		.then(() => {
			// Notification handling moved to main.tsx - remove these listeners
			// FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
			// 	alert(JSON.stringify(event, null, 2));
			// 	const route = (event.notification.data as Record<string, unknown>)?.["route"];
			// 	if (typeof route === "string") {
			// 		notificationRedirect(route);
			// 	}
			// });
			// OneSignal.Notifications.addEventListener("click", (event) => {
			// 	console.log("Notification received:", event);
			// 	const route = (event.notification.additionalData as Record<string, unknown>)?.["route"];
			// 	if (typeof route === "string") {
			// 		notificationRedirect(route);
			// 	}
			// });
		})
		.then(() => {
			// App URL handling also moved to main.tsx
			// App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
			// 	console.log("appUrlOpen", { event });
			// 	const slug = event.url.split(".app").pop();
			// 	if (slug) {
			// 		notificationRedirect(slug);
			// 	}
			// });
		})
		.catch((error) => {
			console.error("Error checking push notification permissions:", error);
		});
};

export const AppHeader = (): ReactElement => {
	const [nativePermission, setNativePermission] = useState<
		"granted" | "denied" | "prompt" | "prompt-with-rationale" | "unknown"
	>("unknown");
	const [notificationCenterStatus, setNotificationCenterStatus] = useState<
		"ready" | "notReady"
	>("notReady");
	const { data: notificationsHistory } = useQuery<NotificationHistory>({
		queryKey: ["notificationsHistory"],
		queryFn: getNotificationsHistory,
		enabled:
			nativePermission === "granted" && notificationCenterStatus === "ready",
	});

	const { isOnline } = useOnlineStatusContext();
	const os = useOs();
	const [installPrompt, setInstallPrompt] = useState<Event | null>(null);

	const navigate = useNavigate();

	const handleNotificationTap = (route: string): void => {
		if (route) {
			navigate({ to: route });
		}
	};

	// ONE_SIGNAL
	// useEffect(() => {
	// 	notificationPermission(
	// 		setNativePermission,
	// 		handleNotificationTap,
	// 		// refetch,
	// 		setNotificationCenterStatus
	// 	);

	// 	return (): void => {
	// 		FirebaseMessaging.removeAllListeners();
	// 	};
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [nativePermission]);

	useEffect(() => {
		window.addEventListener("beforeinstallprompt", (event) => {
			event.preventDefault();
			setInstallPrompt(event);
		});

		return (): void => {
			window.removeEventListener("beforeinstallprompt", (event) => {
				event.preventDefault();
				setInstallPrompt(event);
			});
		};
	}, []);

	const disableInAppInstallPrompt = (): void => {
		setInstallPrompt(null);
	};

	const installPWA = async (): Promise<void> => {
		console.log("Zainstaluj");

		if (installPrompt === null) {
			return;
		}
		try {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
			const result = await installPrompt.prompt();
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			console.log(`Install prompt was: ${result.outcome}`);
			disableInAppInstallPrompt();
		} catch (error) {
			console.error("Error installing PWA:", error);
		}
	};

	const isDesktop = os === "windows" || os === "macos" || os === "linux";

	return (
		<>
			<header className="sticky top-0 flex lg:justify-center items-center h-[88px] p-6 pl-3 pt-safe-offset-6 gap-4 bg-white z-20">
				<NavigationMVP counterBadge={notificationsHistory?.counterBadge} />
				{!isOnline && <BrokenNetworkConnectionIcon className="" />}
			</header>
			{(nativePermission === "prompt" ||
				nativePermission === "prompt-with-rationale") && (
				<div className="flex justify-between items-center bg-seconadaryBackground w-full p-3 text-sm text-fontseconadry">
					<div>
						<h4 className="font-bold">Bądź na bieżąco</h4>
					</div>
					<Button
						className="border-[1px] border-slate-200 bg-gradient-to-r from-[#0059FF] to-[#20314F] text-white"
						variant="ghost"
						onClick={() => {
							notificationPermission(
								setNativePermission,
								handleNotificationTap,
								// refetch,
								setNotificationCenterStatus
							);
						}}
					>
						Włącz powiadomienia
					</Button>
					v1
				</div>
			)}
			{isDesktop && installPrompt !== null && (
				<div className="bg-seconadaryBackground w-full text-sm text-fontseconadry">
					<div className="flex justify-between items-center max-w-[890px] mx-auto p-3">
						<h4 className="font-bold">
							Zainstaluj aplikację Akademia LTCA na swoim komputerze
						</h4>
						<Button
							className="border-[1px] border-slate-200 bg-gradient-to-r from-[#0059FF] to-[#20314F] text-white"
							variant="ghost"
							onClick={installPWA}
						>
							Instalacja
						</Button>
					</div>
				</div>
			)}
		</>
	);
};
